import { ADVISORY_STATUS, EMPTY_ADVISORY_FORM_VALUES } from '../../constants/firmware-advisories';
import { approvedVersionOf, findComponentTypeById } from '../../helpers/hardwareInventory';

/*
 * Pure transforms between the advisory form and the shapes the advisory endpoints take.
 *
 * Kept dependency-free on purpose: `moment` and `_` are webpack ProvidePlugin globals and are not
 * defined when mocha loads this module, so anything reachable from a test must not rely on them.
 *
 * This module is the one place the advisory API's read/write asymmetry is handled. Reads nest the
 * match criteria (`manufacturerMatch: {op, value}`); writes take them flat
 * (`manufacturerMatchOp`, `manufacturerMatchValue`). Anywhere else that mismatch would be a bug
 * waiting to be copy-pasted.
 */

const trimmed = (value) => {
	return String(value ?? '').trim();
};

/*
 * A version is an exact, case-preserved string to the API - `D12` and `d12` are different values,
 * matched by equality with no semver logic behind them. So versions are only ever trimmed and
 * de-duplicated here, never lowercased and never sorted.
 */
export const normalizeVersions = (versions) => {
	if (!Array.isArray(versions)) {
		return [];
	}

	return versions.reduce((kept, version) => {
		const value = trimmed(version);

		if (!value || kept.includes(value)) {
			return kept;
		}

		return [...kept, value];
	}, []);
};

export const advisoryToFormValues = (advisory) => {
	if (!advisory) {
		return { ...EMPTY_ADVISORY_FORM_VALUES };
	}

	return {
		componentTypeId: advisory.componentTypeId ?? '',
		manufacturerMatchOp: advisory.manufacturerMatch?.op ?? 'equals',
		manufacturerMatchValue: advisory.manufacturerMatch?.value ?? '',
		modelMatchOp: advisory.modelMatch?.op ?? 'equals',
		modelMatchValue: advisory.modelMatch?.value ?? '',
		affectedVersions: normalizeVersions(advisory.affectedVersions),
		targetVersion: advisory.targetVersion ?? '',
		severity: advisory.severity ?? '',
		kbUrl: advisory.kbUrl ?? '',
		vendorUrl: advisory.vendorUrl ?? ''
	};
};

/*
 * The flat write body, used for both POST and PATCH.
 *
 * PATCH takes every field as optional, but the full set is sent anyway: a partial diff has to
 * answer "was this cleared or left untouched?", and for `affectedVersions` there is no answer that
 * is not a guess. `status` is deliberately absent - revoking is its own call.
 */
export const formValuesToWritePayload = (values) => {
	return {
		componentTypeId: trimmed(values?.componentTypeId),
		manufacturerMatchOp: values?.manufacturerMatchOp ?? 'equals',
		manufacturerMatchValue: trimmed(values?.manufacturerMatchValue),
		modelMatchOp: values?.modelMatchOp ?? 'equals',
		modelMatchValue: trimmed(values?.modelMatchValue),
		affectedVersions: normalizeVersions(values?.affectedVersions),
		targetVersion: trimmed(values?.targetVersion),
		severity: values?.severity ?? '',
		kbUrl: trimmed(values?.kbUrl),
		vendorUrl: trimmed(values?.vendorUrl)
	};
};

export const REVOKE_PAYLOAD = { status: ADVISORY_STATUS.REVOKED };

/*
 * Pre-fills the matcher from the chosen component type.
 *
 * `equals` on both fields, against the catalog's normalized values. Those are already lowercased,
 * and the edge matcher lowercases the reported value before comparing, so equality is an exact and
 * total match. `contains` and `starts_with` are the dangerous defaults - `model contains
 * st4000nm0023` also catches `st4000nm0023a` - and a pre-fill is precisely the moment nobody is
 * reading the operator. Widening stays a deliberate act.
 *
 * Never pre-fill from `displayName` or the `...Raw` fields: those are the pre-normalization strings
 * and will not match what the edge compares.
 *
 * `targetVersion` is the version HITS approved for the type where there is one, and the newest
 * version seen in the fleet only as a fallback. Both claim "the version to be on", but newest-in-fleet
 * only reports what somebody happens to run, while the approval is an actual endorsement.
 *
 * `affectedVersions` is deliberately left alone. "Oldest in the fleet" is one version out of a
 * possibly wide spread, and silently pre-filling an incomplete advisory scope is worse than
 * an empty field. That is about not guessing a value, not about keeping whatever is already there:
 * see `componentTypeChangeFields` for the re-pick case.
 */
export const prefillFromComponentType = (componentType) => {
	return {
		componentTypeId: componentType?.id ?? '',
		manufacturerMatchOp: 'equals',
		manufacturerMatchValue: componentType?.manufacturer ?? '',
		modelMatchOp: 'equals',
		modelMatchValue: componentType?.model ?? '',
		targetVersion: approvedVersionOf(componentType) ?? componentType?.newestFirmwareVersion ?? ''
	};
};

/*
 * The fields to dispatch when the author picks a component type in the form.
 *
 * `null` for a re-select of the same id: react-select fires its change handler even when the option
 * already selected is picked again, and wiping a deliberate version selection on that non-event is
 * worse than the staleness this guards against.
 *
 * On a real change the pre-fill is joined by an empty `affectedVersions`. The versions left over
 * from the previous pick belong to the previous type - they were offered by that type's fleet scan
 * - yet they stay selected and savable, and nothing downstream relates a version to a component
 * type: the validation checks presence and the target-not-in-affected rule, and the API matches
 * version strings by equality without knowing which type they came from.
 */
export const componentTypeChangeFields = (componentTypeId, previousComponentTypeId, componentTypes) => {
	if ((componentTypeId ?? '') === (previousComponentTypeId ?? '')) {
		return null;
	}

	return {
		...prefillFromComponentType(findComponentTypeById(componentTypeId, componentTypes)),
		affectedVersions: []
	};
};

/*
 * The create-mode seed for the advisory offered straight after an approval.
 *
 * The target version is the value the operator just submitted, not the refreshed row's
 * `approvedFirmwareVersion`. `GET /hardware/component-types` is served from
 * `fleet_component_firmware_analytics`, and that table is rebuilt off the
 * `HardwareComponentTypeApproved` event rather than inside the approval job, so a row read straight
 * after a succeeded job can still carry the version that was just replaced.
 */
export const advisorySeedFromApproval = (componentType, approvedVersion) => {
	return {
		...prefillFromComponentType(componentType),
		targetVersion: trimmed(approvedVersion)
	};
};
